import { z } from 'zod';
import {
  energySourceDefinition,
  type CategoryGroup,
  type EnergySource,
  type VehicleCategory,
  type VehicleFacts,
} from './fleet';

/**
 * When a vehicle counts as clean, what share of them has to be, and how the
 * two become a report (docs/clean-vehicles-design.md section 7).
 *
 * ## This is law, so it is code
 *
 * The rules and the targets are catalogs in this file rather than rows in a
 * table, and the reason is not convenience. A table would let somebody redefine
 * compliance without a code review, in a product whose output goes to a
 * regulator. A catalog can be unit-tested against worked examples, which is the
 * only way anybody will ever believe the arithmetic -- see
 * `fleet-compliance.test.ts`, where the examples are the point of the file.
 *
 * ## Everything is evaluated as of a date
 *
 * `evaluate` takes the facts and a date and selects the rule in force. There is
 * no cached verdict anywhere in the system, because the definition of a clean
 * light-duty vehicle changes at the boundary between the two reference periods:
 * the same van is clean under the first and not under the second, and a stored
 * boolean would silently rewrite history the morning the rules changed.
 *
 * ## Three verdicts, not two
 *
 * A vehicle whose CO2 figure was never recorded is not a dirty vehicle. It is
 * one the authority cannot report on, and calling it non-compliant would send
 * somebody chasing an emissions problem that is really a data-entry problem.
 * `indeterminate` is counted separately, listed separately, and is what the
 * data-quality tile exists to drive down.
 *
 * ## What must be checked before this ships
 *
 * The numbers below -- period boundaries, thresholds, and every target
 * percentage -- were written from the directive as understood at design time
 * and have NOT been confirmed against the consolidated text by anybody with
 * legal responsibility. `TARGETS_REVIEWED` records that. The report refuses to
 * present itself as a compliance statement while it is false, which is a
 * deliberate choice: a plausible-looking percentage is more dangerous than a
 * missing one.
 */

/* ------------------------------------------------------- reference periods */

export interface ReferencePeriod {
  id: string;
  labelKey: string;
  /** Inclusive, ISO date. An agreement's award date falls in the period whose
   *  bounds contain it. */
  from: string;
  /** Inclusive. */
  until: string;
}

export const REFERENCE_PERIODS = [
  {
    id: '2021-2025',
    labelKey: 'fleet.period.first',
    from: '2021-08-02',
    until: '2025-12-31',
  },
  {
    id: '2026-2030',
    labelKey: 'fleet.period.second',
    from: '2026-01-01',
    until: '2030-12-31',
  },
] as const satisfies readonly ReferencePeriod[];

export type ReferencePeriodId = (typeof REFERENCE_PERIODS)[number]['id'];
export const referencePeriodIdSchema = z.enum(
  REFERENCE_PERIODS.map((period) => period.id) as [string, ...string[]],
);

/**
 * Which period an award date falls in, or null when it falls in none.
 *
 * Null is a real answer and not an error: an agreement awarded before the
 * directive applied is a genuine row that simply counts towards nothing. The
 * report shows those in their own group rather than dropping them, because "why
 * is this contract missing from the report" is otherwise unanswerable.
 */
export function referencePeriodFor(awardedOn: string | null): ReferencePeriod | null {
  if (!awardedOn) return null;
  return (
    REFERENCE_PERIODS.find((period) => awardedOn >= period.from && awardedOn <= period.until) ??
    null
  );
}

export function getReferencePeriod(id: string): ReferencePeriod | null {
  return REFERENCE_PERIODS.find((period) => period.id === id) ?? null;
}

/* ------------------------------------------------------------- the rules */

/**
 * The tests a rule can apply, as a closed union.
 *
 * A closed union rather than a predicate function per rule, so that the catalog
 * stays data: a rule set is something to read and check against the directive,
 * not something to trace through. Adding a genuinely new kind of test is a
 * change here and in `applyTest`, and both are visible in one diff.
 */
export type CleanVehicleTest =
  /** Zero emission at the tailpipe: no CO2, by whichever measure applies. */
  | { kind: 'zero_emission' }
  /** The first period's light-duty test: a CO2 ceiling *and* a real-driving
   *  emissions ceiling expressed as a share of the applicable limit. Both must
   *  be known and both must pass. */
  | { kind: 'co2_and_rde'; maxCo2GramsPerKm: number; maxRdeSharePercent: number }
  /** The heavy-duty test: the vehicle runs on an alternative fuel. */
  | { kind: 'alternative_fuel'; excludeHighIlucRisk: boolean };

export interface CleanVehicleRule {
  id: string;
  /** What to call it on screen. A key rather than a name, for the same reason
   *  the periods and the fuels carry one: the catalog is read in three
   *  languages, and the rules screen renders it as prose. */
  labelKey: string;
  appliesTo: readonly VehicleCategory[];
  /** Inclusive ISO dates. Selected by the date the verdict is asked for. */
  from: string;
  until: string;
  test: CleanVehicleTest;
}

export const CLEAN_VEHICLE_RULES = [
  {
    id: 'light-duty-period-1',
    labelKey: 'fleet.rules.name.lightDutyPeriod1',
    appliesTo: ['M1', 'M2', 'N1'],
    from: '2021-08-02',
    until: '2025-12-31',
    test: { kind: 'co2_and_rde', maxCo2GramsPerKm: 50, maxRdeSharePercent: 80 },
  },
  {
    id: 'light-duty-period-2',
    labelKey: 'fleet.rules.name.lightDutyPeriod2',
    appliesTo: ['M1', 'M2', 'N1'],
    from: '2026-01-01',
    until: '2030-12-31',
    test: { kind: 'zero_emission' },
  },
  {
    id: 'heavy-duty-alternative-fuels',
    labelKey: 'fleet.rules.name.heavyDuty',
    appliesTo: ['M3', 'N2', 'N3'],
    from: '2021-08-02',
    until: '2030-12-31',
    test: { kind: 'alternative_fuel', excludeHighIlucRisk: true },
  },
] as const satisfies readonly CleanVehicleRule[];

export const CLEAN_VERDICTS = ['clean', 'not_clean', 'indeterminate'] as const;
export const cleanVerdictSchema = z.enum(CLEAN_VERDICTS);
export type CleanVerdict = (typeof CLEAN_VERDICTS)[number];

export interface CleanEvaluation {
  verdict: CleanVerdict;
  /** The rule that produced it, or null when no rule covers the date. */
  ruleId: string | null;
  /**
   * Why, as a translation key. Present on every verdict rather than only on the
   * unhappy ones, because the vehicle form renders it live as somebody types
   * and "clean: zero emission" is as useful as "we cannot tell: no CO2 figure".
   */
  reasonKey: string;
  /** Whether this vehicle is zero-emission on the same date. Carried separately
   *  because the bus target has a zero-emission half that `clean` alone cannot
   *  answer: a biomethane bus is clean and is not zero-emission. */
  zeroEmission: boolean;
}

function anyZeroEmission(sources: readonly EnergySource[]): boolean {
  return sources.some((source) => energySourceDefinition(source).zeroEmission);
}

/**
 * Zero emission from the recorded figures.
 *
 * Two independent ways to know, and either is enough: a fuel that has no
 * tailpipe, or a measured zero. They disagree only when a record is wrong, and
 * in that case believing the fuel is the safer reading -- a battery vehicle with
 * a mistyped CO2 figure is still a battery vehicle.
 */
function isZeroEmission(facts: VehicleFacts): boolean | null {
  if (anyZeroEmission(facts.energySources)) return true;
  if (facts.co2GramsPerKwh != null) return facts.co2GramsPerKwh <= 1;
  if (facts.co2WltpGramsPerKm != null) return facts.co2WltpGramsPerKm === 0;
  return null;
}

function applyTest(test: CleanVehicleTest, facts: VehicleFacts): Omit<CleanEvaluation, 'ruleId'> {
  const zero = isZeroEmission(facts);

  switch (test.kind) {
    case 'zero_emission': {
      if (zero === null) {
        return {
          verdict: 'indeterminate',
          reasonKey: 'fleet.reason.noEmissionFigure',
          zeroEmission: false,
        };
      }
      return {
        verdict: zero ? 'clean' : 'not_clean',
        reasonKey: zero ? 'fleet.reason.zeroEmission' : 'fleet.reason.notZeroEmission',
        zeroEmission: zero,
      };
    }

    case 'co2_and_rde': {
      /*
       * A zero-emission vehicle passes without an RDE figure. The RDE ceiling
       * is a limit on pollutants a vehicle with no exhaust does not emit, and
       * requiring the number would mark every battery van `indeterminate` --
       * the single most common vehicle this test will ever see.
       */
      if (zero === true) {
        return {
          verdict: 'clean',
          reasonKey: 'fleet.reason.zeroEmission',
          zeroEmission: true,
        };
      }
      if (facts.co2WltpGramsPerKm == null) {
        return {
          verdict: 'indeterminate',
          reasonKey: 'fleet.reason.noCo2Figure',
          zeroEmission: false,
        };
      }
      if (facts.co2WltpGramsPerKm > test.maxCo2GramsPerKm) {
        return {
          verdict: 'not_clean',
          reasonKey: 'fleet.reason.co2AboveLimit',
          zeroEmission: false,
        };
      }
      if (facts.maxRdeNoxPercent == null) {
        return {
          verdict: 'indeterminate',
          reasonKey: 'fleet.reason.noRdeFigure',
          zeroEmission: false,
        };
      }
      if (facts.maxRdeNoxPercent > test.maxRdeSharePercent) {
        return {
          verdict: 'not_clean',
          reasonKey: 'fleet.reason.rdeAboveLimit',
          zeroEmission: false,
        };
      }
      return {
        verdict: 'clean',
        reasonKey: 'fleet.reason.withinCo2AndRde',
        zeroEmission: false,
      };
    }

    case 'alternative_fuel': {
      const sources = facts.energySources;
      if (sources.length === 0) {
        return {
          verdict: 'indeterminate',
          reasonKey: 'fleet.reason.noEnergySource',
          zeroEmission: false,
        };
      }
      const definitions = sources.map(energySourceDefinition);
      const alternatives = definitions.filter((definition) => definition.alternative);
      if (alternatives.length === 0) {
        return {
          verdict: 'not_clean',
          reasonKey: 'fleet.reason.conventionalFuel',
          zeroEmission: false,
        };
      }
      /*
       * The ILUC exclusion needs the feedstock, which this product does not
       * collect. Rather than assume the favourable answer, a vehicle whose only
       * qualifying fuel is one that *may* be high-ILUC-risk is reported as
       * undetermined -- which is what sends somebody to ask the supplier, and
       * is the honest state of the record either way.
       */
      if (
        test.excludeHighIlucRisk &&
        alternatives.every((definition) => definition.ilucSensitive)
      ) {
        return {
          verdict: 'indeterminate',
          reasonKey: 'fleet.reason.feedstockUnknown',
          zeroEmission: false,
        };
      }
      return {
        verdict: 'clean',
        reasonKey: 'fleet.reason.alternativeFuel',
        zeroEmission: zero === true,
      };
    }
  }
}

/**
 * The rule in force for a category on a date, or null when none is.
 *
 * Extracted from `evaluate` rather than written a second time beside it,
 * because the screens that *explain* a verdict have to select the same rule
 * that produced it. A second `find` with its own bounds comparison is how a
 * screen ends up confidently explaining a rule that did not decide the answer
 * -- most likely on the two days of the year either comparison is off by one.
 */
export function ruleFor(category: VehicleCategory, onDate: string): CleanVehicleRule | null {
  /* `appliesTo` is a readonly tuple of literals on each catalog entry, so the
     union across entries narrows `includes`'s parameter to `never`. Widened
     here rather than loosening the catalog's own types, which are what make the
     entries readable as data. */
  return (
    (CLEAN_VEHICLE_RULES as readonly CleanVehicleRule[]).find(
      (candidate) =>
        candidate.appliesTo.includes(category) &&
        onDate >= candidate.from &&
        onDate <= candidate.until,
    ) ?? null
  );
}

/** The rule an evaluation names in `ruleId`, so a screen holding a verdict can
 *  reach the rule behind it without repeating the date arithmetic. */
export function getCleanVehicleRule(id: string): CleanVehicleRule | null {
  return (
    (CLEAN_VEHICLE_RULES as readonly CleanVehicleRule[]).find((rule) => rule.id === id) ?? null
  );
}

/**
 * The verdict for a vehicle on a date.
 *
 * `onDate` is the date the question is asked *about*, not today: a report for
 * the first reference period asks about a date inside it, whenever it is run.
 */
export function evaluate(facts: VehicleFacts, onDate: string): CleanEvaluation {
  const rule = ruleFor(facts.category, onDate);

  if (!rule) {
    return {
      verdict: 'indeterminate',
      ruleId: null,
      reasonKey: 'fleet.reason.noRuleForDate',
      zeroEmission: isZeroEmission(facts) === true,
    };
  }

  return { ruleId: rule.id, ...applyTest(rule.test, facts) };
}

/* ------------------------------------------------- the rules, in words */

/**
 * One sentence of a rule, and the figures that sentence quotes.
 *
 * `values` is read off the same `test` object `applyTest` compares against,
 * never written out again beside it. That is the whole discipline of this
 * section, and the reason it lives in this file rather than in the screen that
 * renders it: an explanation of a rule is a second statement of the rule, and
 * the way a second statement goes wrong is somebody changing 50 to 45 in the
 * catalog above and leaving a screen below still saying 50 -- a wrong figure,
 * presented by this product as the law, to the person filing against it.
 *
 * `fleet-compliance.test.ts` asserts every number here came off the test.
 */
export interface RuleRequirement {
  labelKey: string;
  /** Interpolation values for the sentence. Empty where it quotes no figure. */
  values: Record<string, number>;
}

/**
 * A rule as a reader meets it: the ways to pass, and the ways to be unjudgeable.
 *
 * Two lists rather than one, because they are two different things to do
 * something about. A vehicle that fails is one to replace at the next
 * procurement, months of planning away; a vehicle nobody can judge is a field
 * somebody can fill in this afternoon. Flattening them into "did not count"
 * loses the half that is actionable today, which is the same mistake
 * `indeterminate` exists in `CleanVerdict` to prevent.
 *
 * There is deliberately no `notCleanWhen`. It is the complement of `cleanWhen`
 * once the figures are known, and stating it separately would be a third copy
 * of one piece of logic to keep in step with the other two.
 */
export interface RuleExplanation {
  /** Meeting any one of these is enough on its own. */
  cleanWhen: readonly RuleRequirement[];
  /** What makes the verdict `indeterminate` rather than a pass or a fail. */
  undeterminedWhen: readonly RuleRequirement[];
}

/**
 * What a rule demands, in sentences a procurement officer can check us on.
 *
 * A closed switch over the same union `applyTest` switches on, so a genuinely
 * new kind of test is a compile error here as well as there -- the two halves
 * of "what the rule does" and "what we tell people it does" cannot be added to
 * one at a time.
 */
export function explainRule(rule: CleanVehicleRule): RuleExplanation {
  const test = rule.test;

  switch (test.kind) {
    case 'zero_emission':
      return {
        cleanWhen: [{ labelKey: 'fleet.rules.clean.zeroEmission', values: {} }],
        undeterminedWhen: [{ labelKey: 'fleet.rules.undetermined.noEmissionFigure', values: {} }],
      };

    case 'co2_and_rde':
      return {
        cleanWhen: [
          /* The shortcut is listed first because it is the common case and
             because it is the one nobody expects: a battery van passes with
             neither figure recorded, and a reader who does not know that goes
             looking for an RDE number that does not exist for it. */
          { labelKey: 'fleet.rules.clean.zeroEmissionShortcut', values: {} },
          {
            labelKey: 'fleet.rules.clean.co2AndRde',
            values: { co2: test.maxCo2GramsPerKm, rde: test.maxRdeSharePercent },
          },
        ],
        undeterminedWhen: [{ labelKey: 'fleet.rules.undetermined.missingFigure', values: {} }],
      };

    case 'alternative_fuel':
      return {
        /* The qualifying fuels are not named in the sentence. They are a
           catalog of their own, rendered as a table beside this one, and a
           sentence listing them would be the drift this section exists to
           avoid -- in the one direction the catalog is most likely to grow. */
        cleanWhen: [{ labelKey: 'fleet.rules.clean.alternativeFuel', values: {} }],
        undeterminedWhen: [
          { labelKey: 'fleet.rules.undetermined.noEnergySource', values: {} },
          ...(test.excludeHighIlucRisk
            ? [{ labelKey: 'fleet.rules.undetermined.feedstock', values: {} }]
            : []),
        ],
      };
  }
}

/* ----------------------------------------------------------- the targets */

/**
 * Whether the target figures below have been checked by somebody who can read
 * the directive as law.
 *
 * False, and it is a constant rather than a comment because the report reads
 * it: while it is false, every response carries `provisional: true` and the
 * screen says so in words. Flipping it is a one-line change that should be made
 * in the same commit as the legal review, by whoever did the review.
 */
export const TARGETS_REVIEWED = false;

export interface CategoryTarget {
  /** Minimum share of clean vehicles, as a percentage. */
  minimumCleanPercent: number;
  /**
   * For buses: the part of the target that must additionally be zero-emission,
   * as a percentage of the same denominator. Half the headline target in the
   * directive's scheme. Null where the group has no such sub-target.
   */
  minimumZeroEmissionPercent: number | null;
}

export type CountryTargets = Record<ReferencePeriodId, Record<CategoryGroup, CategoryTarget>>;

/**
 * Minimum shares per Member State.
 *
 * Keyed by ISO 3166-1 alpha-2 so the same module serves a Swedish authority by
 * reading a different row -- nothing in the calculation is Finland-specific.
 * Only the countries a deployment has actually asked for are listed; an unknown
 * country produces a report with counts and no targets, which is honest, rather
 * than a report against somebody else's numbers.
 */
export const COUNTRY_TARGETS: Record<string, CountryTargets> = {
  FI: {
    '2021-2025': {
      light_duty: { minimumCleanPercent: 38.5, minimumZeroEmissionPercent: null },
      trucks: { minimumCleanPercent: 9, minimumZeroEmissionPercent: null },
      buses: { minimumCleanPercent: 41, minimumZeroEmissionPercent: 20.5 },
    },
    '2026-2030': {
      light_duty: { minimumCleanPercent: 38.5, minimumZeroEmissionPercent: null },
      trucks: { minimumCleanPercent: 15, minimumZeroEmissionPercent: null },
      buses: { minimumCleanPercent: 59, minimumZeroEmissionPercent: 29.5 },
    },
  },
  SE: {
    '2021-2025': {
      light_duty: { minimumCleanPercent: 38.5, minimumZeroEmissionPercent: null },
      trucks: { minimumCleanPercent: 10, minimumZeroEmissionPercent: null },
      buses: { minimumCleanPercent: 45, minimumZeroEmissionPercent: 22.5 },
    },
    '2026-2030': {
      light_duty: { minimumCleanPercent: 38.5, minimumZeroEmissionPercent: null },
      trucks: { minimumCleanPercent: 15, minimumZeroEmissionPercent: null },
      buses: { minimumCleanPercent: 65, minimumZeroEmissionPercent: 32.5 },
    },
  },
};

export function targetsFor(
  countryCode: string,
  periodId: string,
  group: CategoryGroup,
): CategoryTarget | null {
  const country = COUNTRY_TARGETS[countryCode.toUpperCase()];
  if (!country) return null;
  const period = country[periodId as ReferencePeriodId];
  if (!period) return null;
  return period[group];
}

/* ------------------------------------------------------------ the report */

/**
 * How the report is grouped.
 *
 * One parameter rather than three endpoints: the arithmetic is identical and
 * only the key changes. What is *not* identical is what the numbers may be
 * called -- see `ComplianceGroup.kind` below.
 */
export const COMPLIANCE_GROUPINGS = ['category', 'procurement_unit', 'agreement'] as const;
export const complianceGroupingSchema = z.enum(COMPLIANCE_GROUPINGS);
export type ComplianceGrouping = (typeof COMPLIANCE_GROUPINGS)[number];

export const complianceQuerySchema = z.object({
  period: z.string().max(32).optional(),
  groupBy: complianceGroupingSchema.optional(),
});
export type ComplianceQuery = z.infer<typeof complianceQuerySchema>;

/**
 * One row of counts.
 *
 * `total` is the denominator: distinct vehicles declared onto in-scope
 * agreements awarded in the period, counted once per agreement. `indeterminate`
 * is a subset of `total` -- an undetermined vehicle stays in the denominator,
 * because the directive counts vehicles rather than vehicles we managed to
 * classify, and moving it out would flatter the share.
 */
export const complianceCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  clean: z.number().int().nonnegative(),
  zeroEmission: z.number().int().nonnegative(),
  indeterminate: z.number().int().nonnegative(),
  cleanPercent: z.number(),
  zeroEmissionPercent: z.number(),
});
export type ComplianceCounts = z.infer<typeof complianceCountsSchema>;

export const complianceTargetSchema = z.object({
  minimumCleanPercent: z.number(),
  minimumZeroEmissionPercent: z.number().nullable(),
  /** Percentage points above or below. Negative is short. */
  cleanAttainmentPoints: z.number(),
  zeroEmissionAttainmentPoints: z.number().nullable(),
  /**
   * Whether the target was met -- and `null` when there is nothing to judge.
   *
   * Three-valued for the same reason `indeterminate` exists beside `clean` and
   * `not_clean`: an authority that procured no lorries in a period has not
   * missed the lorry target, it has incurred no obligation against it. A
   * boolean would render that as a red cross, which is a false statement this
   * software would be making about its own client -- the exact failure mode
   * section 7.5 exists to prevent, arriving through the back door of an empty
   * denominator rather than through the wrong grouping.
   */
  met: z.boolean().nullable(),
});
export type ComplianceTarget = z.infer<typeof complianceTargetSchema>;

/**
 * A group of the report, and the single most important type in this file.
 *
 * `kind` decides what the figures may be *called*, and it is carried on the
 * data rather than inferred by the screen. The directive's minimum shares apply
 * to the contracting authority over a reference period; they are not a test any
 * one department or contract has to pass. So:
 *
 *  - `compliance` rows (grouped by category) carry a `target` and may be
 *    rendered as met or missed.
 *  - `contribution` rows (grouped by unit or agreement) carry no target at all,
 *    and the API refuses to compute one for them.
 *
 * Rendering a red "missed target" against one department would be a legally
 * false statement made by this software about its own client, and it is exactly
 * what falls out of reusing a table component without thinking. Making the two
 * different types is how the type system refuses to let that happen.
 */
export const complianceGroupSchema = z.object({
  key: z.string(),
  labelKey: z.string().nullable(),
  label: z.string().nullable(),
  /** Which category group these counts are for. Every grouping splits by
   *  category underneath, because a target only exists per category and a row
   *  mixing lorries with vans is not a number anybody can act on. */
  categoryGroup: z.enum(['light_duty', 'trucks', 'buses']),
  counts: complianceCountsSchema,
  kind: z.enum(['compliance', 'contribution']),
  target: complianceTargetSchema.nullable(),
  /** Contribution rows only: this row's share of the authority's denominator
   *  for the same category group, as a percentage. What makes a per-unit view
   *  actionable rather than merely sliced. */
  shareOfDenominatorPercent: z.number().nullable(),
});
export type ComplianceGroup = z.infer<typeof complianceGroupSchema>;

export const complianceReportSchema = z.object({
  periodId: z.string(),
  periodFrom: z.string(),
  periodUntil: z.string(),
  countryCode: z.string(),
  groupBy: complianceGroupingSchema,
  groups: z.array(complianceGroupSchema),
  /** Agreements counted, and the ones deliberately not. */
  agreementsInScope: z.number().int().nonnegative(),
  agreementsOutOfScope: z.number().int().nonnegative(),
  agreementsWithoutAwardDate: z.number().int().nonnegative(),
  /** True while `TARGETS_REVIEWED` is false, or when no targets exist for the
   *  configured country. The screen must say so; see the constant. */
  provisional: z.boolean(),
  provisionalReasonKey: z.string().nullable(),
  generatedAt: z.string(),
});
export type ComplianceReport = z.infer<typeof complianceReportSchema>;

/** Percentage to one decimal place, and zero rather than NaN for an empty
 *  denominator: a category with no vehicles is 0% clean of nothing, and a NaN
 *  reaching the browser renders as a blank cell nobody can interpret. */
export function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

export function countsFrom(
  evaluations: readonly Pick<CleanEvaluation, 'verdict' | 'zeroEmission'>[],
): ComplianceCounts {
  const total = evaluations.length;
  const clean = evaluations.filter((entry) => entry.verdict === 'clean').length;
  const zeroEmission = evaluations.filter(
    (entry) => entry.verdict === 'clean' && entry.zeroEmission,
  ).length;
  const indeterminate = evaluations.filter((entry) => entry.verdict === 'indeterminate').length;

  return {
    total,
    clean,
    zeroEmission,
    indeterminate,
    cleanPercent: percent(clean, total),
    zeroEmissionPercent: percent(zeroEmission, total),
  };
}

/**
 * The attainment for a compliance row.
 *
 * Only ever called for `kind: 'compliance'`. A contribution row has no target by
 * construction, and there is deliberately no code path that would give it one.
 */
export function attainmentFor(counts: ComplianceCounts, target: CategoryTarget): ComplianceTarget {
  const cleanAttainmentPoints =
    Math.round((counts.cleanPercent - target.minimumCleanPercent) * 10) / 10;
  const zeroEmissionAttainmentPoints =
    target.minimumZeroEmissionPercent == null
      ? null
      : Math.round((counts.zeroEmissionPercent - target.minimumZeroEmissionPercent) * 10) / 10;

  return {
    minimumCleanPercent: target.minimumCleanPercent,
    minimumZeroEmissionPercent: target.minimumZeroEmissionPercent,
    cleanAttainmentPoints,
    zeroEmissionAttainmentPoints,
    met:
      counts.total === 0
        ? null
        : cleanAttainmentPoints >= 0 && (zeroEmissionAttainmentPoints ?? 0) >= 0,
  };
}

/**
 * The date a vehicle's cleanliness is judged on, for a given agreement.
 *
 * The award date, clamped into the period: what the directive asks is whether
 * the vehicles procured by that contract were clean under the rules in force
 * when it was awarded. Using today's date would mean a 2023 contract silently
 * failing in 2026 because the rules moved, which is the opposite of what a
 * reference period is for.
 */
export function judgementDateFor(awardedOn: string, period: ReferencePeriod): string {
  if (awardedOn < period.from) return period.from;
  if (awardedOn > period.until) return period.until;
  return awardedOn;
}
